import { AccessPolicyInteraction, satisfiedAccessPolicy } from '@medplum/core';
import type { AccessPolicy, Resource } from '@medplum/fhirtypes';
import isNil from 'lodash/isNil';

import type { FieldAccess, ViewKind } from '../core/types';

import { type ChoiceValue, getChoiceValue } from './choice';
import {
  type PrimitiveExtension,
  getDataAbsentReason,
  getUnderscoreSibling,
} from './underscore';

/**
 * Closed field-level presentation enum. Views switch on this, so it is API.
 * Every absent-field mechanism collapses into one of these; a value the server
 * actually returned is always `present` (reality wins).
 */
export type FieldPresentationState =
  | { kind: 'present' }
  | { kind: 'not-recorded' }
  | { kind: 'benign-dar'; reason: string }
  | { kind: 'withheld'; mechanism: 'policy' | 'masked' };

type AbsentFieldPresentationState = Exclude<
  FieldPresentationState,
  { kind: 'present' }
>;

/**
 * A field presentation result whose value can only exist in the `present`
 * arm. Consumers cannot accidentally render an absent optional value while
 * separately claiming that the field is present.
 */
export type ResolvedField<T> =
  | { state: { kind: 'present' }; value: T }
  | { state: AbsentFieldPresentationState };

/** Curated-field inclusion policy. This is separate from missing-state resolution. */
export type FieldVisibility =
  | 'always'
  | 'when-present'
  | 'when-present-or-withheld';

/** How an empty array supplied to the collection resolver should be interpreted. */
export type CollectionEmptySemantics = 'absent' | 'visible';

/**
 * Reality-wins ordering: a present value short-circuits before any policy/DAR
 * consideration. For absent fields, in-band DAR outranks the client-computed
 * policy guess; a `masked` DAR and a MASKED label both mean "masked at the
 * source".
 */
export function resolveFieldState(input: {
  present: boolean;
  dataAbsentReason?: string;
  policyHidden?: boolean;
  maskedLabel?: boolean;
}): FieldPresentationState {
  if (input.present) return { kind: 'present' };
  if (input.dataAbsentReason === 'masked') {
    return { kind: 'withheld', mechanism: 'masked' };
  }
  if (input.dataAbsentReason) {
    return { kind: 'benign-dar', reason: input.dataAbsentReason };
  }
  if (input.policyHidden) return { kind: 'withheld', mechanism: 'policy' };
  if (input.maskedLabel) return { kind: 'withheld', mechanism: 'masked' };
  return { kind: 'not-recorded' };
}

/**
 * The resource's read-policy decision as a field-token set.
 * `satisfiedAccessPolicy` runs the compiled client policy against this
 * resource; its `hiddenFields` are normalized so a caller can match by the
 * bare element name, the `[x]`-stripped choice base, or the full `Type.path`.
 */
export function computeFieldAccess(
  resource: Resource,
  policy: AccessPolicy | undefined,
): FieldAccess | undefined {
  if (!policy) return undefined;
  const satisfied = satisfiedAccessPolicy(
    resource,
    AccessPolicyInteraction.READ,
    policy,
  );
  const hidden = satisfied?.hiddenFields ?? [];
  const tokens = new Set<string>();
  for (const path of hidden) {
    const normalizedPath = path.replace(/\[x\]$/, '');
    tokens.add(path);
    tokens.add(normalizedPath);
    const relativePath = path.startsWith(`${resource.resourceType}.`)
      ? path.slice(resource.resourceType.length + 1)
      : path;
    tokens.add(relativePath);
    tokens.add(relativePath.replace(/\[x\]$/, ''));
    const last = path.split('.').pop();
    if (last) {
      tokens.add(last);
      tokens.add(last.replace(/\[x\]$/, ''));
    }
  }
  return { hiddenFields: tokens };
}

export function isFieldHidden(
  access: FieldAccess | undefined,
  ...candidates: string[]
): boolean {
  if (!access) return false;
  return candidates.some((c) => access.hiddenFields.has(c));
}

function fieldPathCandidates(resource: Resource, path: string): string[] {
  const relative = path.startsWith(`${resource.resourceType}.`)
    ? path.slice(resource.resourceType.length + 1)
    : path;
  const last = relative.split('.').pop() ?? relative;
  const bare = last.replace(/\[x\]$/, '');
  const qualified = `${resource.resourceType}.${relative}`;
  const candidates = [
    path,
    path.replace(/\[x\]$/, ''),
    relative,
    relative.replace(/\[x\]$/, ''),
    qualified,
    qualified.replace(/\[x\]$/, ''),
  ];
  if (!relative.includes('.')) candidates.push(last, bare);
  return candidates;
}

function isChoiceFieldHidden(input: {
  access: FieldAccess | undefined;
  resource: Resource;
  base: string;
  path: string;
  concreteKey?: string;
}): boolean {
  const { access, resource, base, path, concreteKey } = input;
  if (!access) return false;
  const concretePath = concreteKey
    ? `${path.slice(0, Math.max(0, path.length - base.length))}${concreteKey}`
    : undefined;
  if (
    isFieldHidden(
      access,
      ...fieldPathCandidates(resource, path),
      ...fieldPathCandidates(resource, `${path}[x]`),
      ...(concretePath ? fieldPathCandidates(resource, concretePath) : []),
    )
  ) {
    return true;
  }

  // A completely stripped choice has no concrete instance key. AccessPolicy
  // may still name a concrete choice path (for example valueQuantity), so a
  // capitalized suffix is treated as a concrete arm of this known choice base.
  const relativePath = path.startsWith(`${resource.resourceType}.`)
    ? path.slice(resource.resourceType.length + 1)
    : path;
  return [...access.hiddenFields].some((hiddenPath) => {
    const relativeHiddenPath = hiddenPath.startsWith(
      `${resource.resourceType}.`,
    )
      ? hiddenPath.slice(resource.resourceType.length + 1)
      : hiddenPath;
    return (
      relativeHiddenPath.startsWith(relativePath) &&
      /^[A-Z]/.test(
        relativeHiddenPath.slice(relativePath.length, relativePath.length + 1),
      )
    );
  });
}

const MASKED_CODE = 'MASKED';

/** MASKED confidentiality label (HL7 v3) carried on either `meta.security` or `meta.tag` — matched by code. */
export function hasMaskedLabel(resource: Resource): boolean {
  const labels = [
    ...(resource.meta?.security ?? []),
    ...(resource.meta?.tag ?? []),
  ];
  return labels.some((coding) => coding.code === MASKED_CODE);
}

type ResolveFieldInput<T> = {
  resource: Resource;
  path: string;
  value: T | null | undefined;
  access?: FieldAccess;
  dataAbsentReason?: string;
  policyCandidates?: readonly string[];
};

/**
 * Resolves an already-selected field value. Present values win over policy,
 * DAR, and resource masking; absent values are classified without inventing a
 * value for the caller to format.
 */
export function resolveField<T>(input: ResolveFieldInput<T>): ResolvedField<T> {
  const present = !isNil(input.value);
  const state = resolveFieldState({
    present,
    dataAbsentReason: input.dataAbsentReason,
    policyHidden: isFieldHidden(
      input.access,
      ...fieldPathCandidates(input.resource, input.path),
      ...(input.policyCandidates ?? []),
    ),
    maskedLabel: hasMaskedLabel(input.resource),
  });

  if (state.kind === 'present') {
    return { state, value: input.value as T };
  }
  return { state };
}

/** Resolves a primitive field, including its FHIR `_<element>` DAR sibling. */
export function resolvePrimitiveField<T>(
  input: Omit<ResolveFieldInput<T>, 'dataAbsentReason'> & { owner?: object },
): ResolvedField<T> {
  const elementKey = input.path.split('.').pop() ?? input.path;
  const siblingDar = getDataAbsentReason(
    getUnderscoreSibling(input.owner ?? input.resource, elementKey),
  );
  const complexDar = getComplexDataAbsentReason(input.value);
  const dataAbsentReason = siblingDar ?? complexDar;
  const value =
    complexDar && !hasRenderableElementContent(input.value)
      ? undefined
      : input.value;
  return resolveField({
    resource: input.resource,
    path: input.path,
    access: input.access,
    policyCandidates: input.policyCandidates,
    value,
    dataAbsentReason,
  });
}

function getComplexDataAbsentReason(value: unknown): string | undefined {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
    ? getDataAbsentReason(value as PrimitiveExtension)
    : undefined;
}

function hasRenderableElementContent(value: unknown): boolean {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) {
    return true;
  }
  return Object.keys(value).some(
    (key) => key !== 'id' && key !== 'extension' && key !== 'modifierExtension',
  );
}

/** Resolves a `value[x]`-style choice and returns only actual present choices as values. */
export function resolveChoiceField(input: {
  resource: Resource;
  base: string;
  access?: FieldAccess;
  owner?: object;
  path?: string;
}): ResolvedField<ChoiceValue> {
  const choice = getChoiceValue(input.owner ?? input.resource, input.base);
  const complexDar =
    choice && choice.type !== 'absent' && 'value' in choice
      ? getComplexDataAbsentReason(choice.value)
      : undefined;
  const complexDarOnly =
    choice &&
    choice.type !== 'absent' &&
    'value' in choice &&
    complexDar !== undefined &&
    !hasRenderableElementContent(choice.value);
  const value =
    choice?.type === 'absent' || complexDarOnly ? undefined : choice;
  const dataAbsentReason =
    choice?.type === 'absent' ? choice.dataAbsentReason : complexDar;
  const state = resolveFieldState({
    present: value !== undefined,
    dataAbsentReason,
    policyHidden: isChoiceFieldHidden({
      access: input.access,
      resource: input.resource,
      base: input.base,
      path: input.path ?? input.base,
      concreteKey: choice?.key,
    }),
    maskedLabel: hasMaskedLabel(input.resource),
  });

  if (state.kind === 'present') return { state, value: value as ChoiceValue };
  return { state };
}

/**
 * Resolves a repeating field. FHIR instance arrays are normally non-empty;
 * callers that have intentionally filtered a visible collection to zero items
 * can opt into `empty: "visible"` so it remains distinct from true absence.
 */
export function resolveCollectionField<T>(input: {
  resource: Resource;
  path: string;
  value: readonly T[] | null | undefined;
  access?: FieldAccess;
  empty?: CollectionEmptySemantics;
}): ResolvedField<readonly T[]> {
  const value =
    input.value?.length || input.empty === 'visible'
      ? (input.value ?? [])
      : undefined;
  return resolveField({
    resource: input.resource,
    path: input.path,
    value,
    access: input.access,
  });
}

/**
 * Applies a curated field's inclusion policy. Explicit DAR remains visible;
 * withheld-only optional fields surface in read views, while chips remain
 * silent because they do not make a completeness claim.
 */
export function isResolvedFieldVisible<T>(
  field: ResolvedField<T>,
  visibility: FieldVisibility,
  viewKind: ViewKind,
): boolean {
  if (visibility === 'always') return true;
  if (field.state.kind === 'present') return true;
  if (
    visibility === 'when-present-or-withheld' &&
    field.state.kind === 'benign-dar'
  ) {
    return true;
  }
  return (
    visibility === 'when-present-or-withheld' &&
    field.state.kind === 'withheld' &&
    viewKind === 'read'
  );
}

/**
 * Resolves a `value[x]`-style choice field to its presentation state for a
 * curated view. It fuses the three withheld mechanisms — in-band DAR, client
 * policy `hiddenFields`, and the MASKED label — so a curated view can mark a
 * field the server stripped entirely (no value, no sibling), which a plain
 * `getChoiceValue` cannot see. `choice` is the present/absent value (if any)
 * so the caller renders it directly when present.
 */
export function choiceFieldState(
  resource: Resource,
  base: string,
  access: FieldAccess | undefined,
): { choice: ChoiceValue | undefined; state: FieldPresentationState } {
  const choice = getChoiceValue(resource, base);
  const resolved = resolveChoiceField({ resource, base, access });
  return { choice, state: resolved.state };
}
